import React, { useCallback, useId, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { Segmented, SegmentedOption } from '../../../components/primitives/Segmented';
import { pickerMessages } from './pickerMessages';
import { PickerFilter } from './pickerFiles';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the picker; each part uses its own slice of it */
import styles from './ComposerMediaPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PickerSongBarProps {
  items: CatalogItem[];
  song: string | undefined;
  onSong: (uuid: string | undefined) => void;
  filter: PickerFilter;
  onFilter: (filter: PickerFilter) => void;
  /** The label of the empty choice; "Pick a song" by default. The SmartLink Media block says "All songs". */
  noSongLabel?: string;
}

/** The song selector and the Images / Videos chips at the top of the picker. */
const PickerSongBarComponent: React.FC<PickerSongBarProps> = ({
  items,
  song,
  onSong,
  filter,
  onFilter,
  noSongLabel,
}) => {
  const intl = useIntl();
  const selectId = useId();

  const change = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => onSong(event.target.value === '' ? undefined : event.target.value),
    [onSong]
  );

  const options = useMemo<ReadonlyArray<SegmentedOption<PickerFilter>>>(
    () => [
      { value: 'all', label: intl.formatMessage(pickerMessages.filterAll) },
      { value: 'image', label: intl.formatMessage(pickerMessages.filterImages) },
      { value: 'video', label: intl.formatMessage(pickerMessages.filterVideos) },
    ],
    [intl]
  );

  const songOptions = useMemo(
    () =>
      items.map((item) => (
        <option key={item.uuid} value={item.uuid}>
          {`${item.title} — ${item.artistName}`}
        </option>
      )),
    [items]
  );

  return (
    <div className={styles.pickerTop}>
      <div className={styles.songField}>
        <label className={styles.fieldName} htmlFor={selectId}>
          {intl.formatMessage(pickerMessages.song)}
        </label>
        <select
          id={selectId}
          className={styles.songSelect}
          value={song ?? ''}
          data-testid="picker-song"
          onChange={change}
        >
          <option value="">{noSongLabel ?? intl.formatMessage(pickerMessages.songNone)}</option>
          {songOptions}
        </select>
      </div>
      <Segmented
        options={options}
        value={filter}
        onChange={onFilter}
        ariaLabel={intl.formatMessage(pickerMessages.filterLabel)}
      />
    </div>
  );
};

export const PickerSongBar = TypedMemo(PickerSongBarComponent);
